import { defineStore } from 'pinia'
import { ref } from 'vue'
import { auth } from '../firebase'
import { onAuthStateChanged, type User } from 'firebase/auth'
import api from '../api'
import type { UserDto } from '../dtos'

import { useBandStore } from './bandStore'
import { useInviteStore } from './inviteStore'
import { useSongStore } from './songStore'
import { useChatNotificationStore } from './chatNotificationStore'
import { useProfileStore } from './profileStore'
import { useNotificationStore } from './notificationStore'

export const useUserStore = defineStore('user', () => {
    const user = ref<User | null>(null)
    const dbUser = ref<UserDto | null>(null)
    const loading = ref(true)
    const isSyncing = ref(false)
    const pendingJoinedBand = ref<string | null>(null)

    async function syncWithBackend(manualDisplayName?: string) {
        if (!user.value) return
        
        // If already syncing and no new name is provided, just wait for the existing one
        if (isSyncing.value && !manualDisplayName) return

        isSyncing.value = true
        try {
            const token = await user.value.getIdToken()
            // If we have a manual name, use it. Otherwise, try to get it from the Firebase user object
            const nameToSync = manualDisplayName || user.value.displayName
            const nameParam = nameToSync ? `?displayName=${encodeURIComponent(nameToSync)}` : ''
            
            const response = await api.get(`/users/me${nameParam}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
            dbUser.value = response.data
            
            // Wait for initial data only if not already loaded
            const bandStore = useBandStore()
            const inviteStore = useInviteStore()
            const promises = []
            
            if (bandStore.bands.length === 0) promises.push(bandStore.fetchBands())
            if (inviteStore.myInvites.length === 0) promises.push(inviteStore.fetchMyInvites())
            
            if (promises.length > 0) {
                await Promise.all(promises)
            }

            // Redeem pending invite link if one exists
            const pendingToken = localStorage.getItem('pendingInviteToken')
            if (pendingToken) {
                localStorage.removeItem('pendingInviteToken')
                try {
                    const redeemResponse = await api.post(`/band-invite-links/redeem/${pendingToken}`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                    const result = redeemResponse.data
                    if (!result.alreadyMember) {
                        // Refresh bands to include the newly joined one
                        const bandStore = useBandStore()
                        await bandStore.fetchBands()
                        const joinedBand = bandStore.bands.find((b: any) => b.id === result.bandId)
                        if (joinedBand) bandStore.setCurrentBand(joinedBand)
                        pendingJoinedBand.value = result.bandName
                    }
                } catch (error) {
                    console.error('Failed to redeem invite link:', error)
                }
            }

            // Fetch user profile (theme, etc.)
            useProfileStore().fetchProfile()

            // Start polling for chat notifications
            useChatNotificationStore().startPolling()
        } catch (error: any) {
            console.error('Failed to sync user with backend:', error)
            // If token is invalid (deleted user, expired session), sign out
            if (error?.response?.status === 401 || error?.code === 'auth/user-token-expired') {
                await auth.signOut()
            }
        } finally {
            isSyncing.value = false
        }
    }

    function init() {
        onAuthStateChanged(auth, async (u) => {
            // If user exists but email not verified, reload to check if they verified since last visit
            if (u && !u.emailVerified && u.providerData[0]?.providerId === 'password') {
                await u.reload()
                // After reload, the user object is mutated in place but Vue won't detect it
                // Re-assign to trigger reactivity
                user.value = auth.currentUser
                u = auth.currentUser!
            } else {
                user.value = u
            }

            if (u && (u.emailVerified || u.providerData[0]?.providerId === 'google.com')) {
                await syncWithBackend()
            } else if (!u) {
                dbUser.value = null
                isSyncing.value = false
                // Clear other stores
                useBandStore().clear()
                useInviteStore().clear()
                useSongStore().clear()
                useChatNotificationStore().clear()
                useProfileStore().clear()
                // Unregister the FCM token for this device so a different user
                // signing in on the same browser doesn't inherit notifications.
                useNotificationStore().clear()
            }
            // Unverified users: don't clear stores, just wait for verification
            loading.value = false
        })
    }

    return { user, dbUser, loading, isSyncing, pendingJoinedBand, init, syncWithBackend }
})
